import * as React from 'react';
import { Alert } from 'reactstrap';
import { NumberInputField } from '../../../components/Forms/fields';
import { CheckInputField } from '../../../components/Forms/fields/CheckInputField';
import { TaskManagementSettingsData } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { SettingsItemContentProps } from '../SettingsItem';

interface TaskManagementSettingsProps extends SettingsItemContentProps {
    values: TaskManagementSettingsData;
    onChange: (settings: TaskManagementSettingsData) => void;
}

export const normalizeClientsFollowUpSettings = (settings: TaskManagementSettingsData) => ({
    taskManagementEnabled: settings.enabled ?? settings.clientsFollowUp?.enabled ?? false,
    enabled: settings.clientsFollowUp?.enabled ?? false,
    daysAfterFirstAppointment: Math.max(1, settings.clientsFollowUp?.daysAfterFirstAppointment ?? 1),
});

const TaskManagementSettings: React.FC<TaskManagementSettingsProps> = ({ values, onChange }) => {
    const t = useTranslations();
    const clientsFollowUp = React.useMemo(() => normalizeClientsFollowUpSettings(values), [values]);

    const onChangeClientsFollowUp = React.useCallback((next: { enabled: boolean; daysAfterFirstAppointment: number }) => {
        onChange({
            ...values,
            clientsFollowUp: {
                enabled: next.enabled,
                daysAfterFirstAppointment: Math.max(1, next.daysAfterFirstAppointment),
            },
        });
    }, [onChange, values]);

    const onChangeTaskManagementEnabled = React.useCallback((enabled: boolean) => {
        onChange({
            ...values,
            enabled,
        });
    }, [onChange, values]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
            <Alert color="secondary" isOpen>
                {t(TK.taskManagementDescription)}
            </Alert>

            <CheckInputField
                id="task-management-enabled"
                label={t(TK.enabled)}
                value={clientsFollowUp.taskManagementEnabled}
                onChange={onChangeTaskManagementEnabled}
            />

            {clientsFollowUp.taskManagementEnabled && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', paddingLeft: 'var(--spacer-M)', borderLeft: 'var(--border-thick) solid var(--appLigthGrey)' }}>
                    <strong>{t(TK.clientsFollowUp)}</strong>

                    <CheckInputField
                        id="clients-follow-up-enabled"
                        label={t(TK.autoCreateClientFollowUpTask)}
                        helpText={t(TK.autoCreateClientFollowUpTaskHelp)}
                        value={clientsFollowUp.enabled}
                        onChange={enabled => onChangeClientsFollowUp({
                            enabled,
                            daysAfterFirstAppointment: values.clientsFollowUp?.daysAfterFirstAppointment ?? 1,
                        })}
                    />

                    {clientsFollowUp.enabled && (
                        <NumberInputField
                            prepend={t(TK.daysAfterFirstAppointment)}
                            append={t(TK.days)}
                            value={clientsFollowUp.daysAfterFirstAppointment}
                            onChange={daysAfterFirstAppointment => onChangeClientsFollowUp({
                                enabled: clientsFollowUp.enabled,
                                daysAfterFirstAppointment: Math.max(1, daysAfterFirstAppointment ?? 1),
                            })}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default TaskManagementSettings;
